import { type MouseEvent, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getExploreTrailUrl, getTrailUrl } from '@alltrails/cards/utils/cardLinks';
import { useClientNavigationPushState } from '@alltrails/core/hooks/useClientNavigation';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import isBrowserHandledClick from '@/utils/isBrowserHandledClick';

/**
 * Builds the explore trail page URL. Takes the same bare trail slug as the cards' own `getTrailUrl`
 * (no `trail/` prefix), so a card's href and its client-side navigation target cannot drift apart.
 */
export const useExploreTrailUrl = () => {
  const languageRegionCode = useLanguageRegionCode();

  return useCallback((slug: string) => getExploreTrailUrl(slug, undefined /* baseUrl */, languageRegionCode), [languageRegionCode]);
};

const useTrailNavigate = () => {
  const languageRegionCode = useLanguageRegionCode();
  const pushState = useClientNavigationPushState();
  const exploreTrailUrl = useExploreTrailUrl();
  const isMobileBrowser = useSelector((state: { context: { mobileBrowser: boolean } }) => state.context?.mobileBrowser);

  return useCallback(
    (slug: string, event?: MouseEvent) => {
      if (isMobileBrowser) {
        window.open(getTrailUrl(slug, undefined /* baseUrl */, languageRegionCode), '_blank');
      } else if (isBrowserHandledClick(event)) {
        window.open(exploreTrailUrl(slug), '_blank');
      } else {
        pushState(exploreTrailUrl(slug));
      }
    },
    [exploreTrailUrl, isMobileBrowser, languageRegionCode, pushState]
  );
};

export default useTrailNavigate;
